// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { SeriesKey } from '../types';
import { bumpChart, type BumpDatum } from '../charts';
import { bumpEntries, yearsIn } from '../analysis';
import { attachSvgZoom, type SvgZoomHandle } from '../utils/svgZoom';
import { segHtml, viewHead } from './shared';

const PALETTE = [
  '#d1613f', '#3d7f9d', '#c9527e', '#3f9d6b', '#8a6bbf', '#bd7a1e', '#2e7d9a', '#b3603f',
  '#6b8e3f', '#a8497a', '#3f7d8a', '#c07d20', '#7a5bbf', '#4f9d6b', '#bf5a4f', '#5a7db0',
];

const SERIES_OPTS: Array<{ id: SeriesKey; label: string }> = [
  { id: 'NSW-boy', label: 'NSW boys' },
  { id: 'NSW-girl', label: 'NSW girls' },
  { id: 'QLD-boy', label: 'QLD boys' },
  { id: 'QLD-girl', label: 'QLD girls' },
];

export function renderCharts(root: HTMLElement, ctx: ViewContext): void {
  const { names } = ctx.data;
  let sk: SeriesKey = 'NSW-girl';
  let topN = 12;
  let zoom: SvgZoomHandle | null = null;

  root.innerHTML = `
    ${viewHead(
      'The Charts',
      'A bump chart of the most popular names over time — each line is a name, and it rises and falls as the name overtakes or is overtaken by others. Hover a line to trace one name through the years; click to open its full profile.',
    )}
    <div class="panel">
      <div class="controls">
        <span class="field-label">List:</span>
        ${segHtml('Series', SERIES_OPTS, sk)}
        <span class="field-label" style="margin-left:var(--space-md)">Show:</span>
        ${segHtml('TopN', [
          { id: '10', label: 'Top 10' },
          { id: '12', label: 'Top 12' },
          { id: '15', label: 'Top 15' },
        ], String(topN))}
      </div>
      <div class="chart-frame" data-role="bump"></div>
      <p class="zoom-hint">Scroll to zoom, drag to pan, double-click to reset. Only names that reached the chosen top rank in some year are drawn.</p>
    </div>
  `;

  const bumpEl = root.querySelector('[data-role="bump"]') as HTMLElement;

  function paint(): void {
    if (zoom) {
      zoom.destroy();
      zoom = null;
    }
    bumpEl.innerHTML = '';
    const years = yearsIn(names, sk);
    const entries = bumpEntries(names, sk, years, topN, topN).slice(0, topN + 4);
    const data: BumpDatum[] = entries.map((e, i) => ({
      key: e.key,
      name: e.name,
      colour: PALETTE[i % PALETTE.length],
      ranks: e.ranks,
    }));
    const svg = bumpChart(data, years, { width: 1120, height: 560, maxRank: topN, onPick: (k) => ctx.openName(k) });
    const scroll = document.createElement('div');
    scroll.className = 'chart-scroll';
    scroll.appendChild(svg);
    bumpEl.appendChild(scroll);
    zoom = attachSvgZoom(svg, { maxScale: 6 });
  }

  root.querySelectorAll('.seg').forEach((seg) => {
    const isSeries = seg.getAttribute('aria-label') === 'Series';
    seg.querySelectorAll('[data-opt]').forEach((b) =>
      b.addEventListener('click', () => {
        seg.querySelectorAll('[data-opt]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        const v = b.getAttribute('data-opt') as string;
        if (isSeries) sk = v as SeriesKey;
        else topN = Number(v);
        paint();
      }),
    );
  });

  paint();
}
